import { format } from 'date-fns';
import { HDate, gematriya} from '@hebcal/core';
import { getDailyZmanim } from '../calendar/zmanim';


export function formatTime(date, formatter) {
  try{  
  // Format the time and ensure there's a space before am/pm
    const formattedTime = format(date, (formatter ? formatter : 'hh:mm aa')).toLowerCase();
    // Add a space before am/pm if needed
    return formattedTime.replace(/(\d+:\d+)([ap]m)/, '$1 $2');
  }catch(err){
    console.error('formatTime', date, err);
    return date;
  }
}



/**
 * Parses a time string (HH:MM with optional am/pm) and returns a new Date object
 * based on the provided baseDate (defaults to today).
 *
 * Examples:
 *   parseTimeToDate("4:45 pm") -> Date object for today at 16:45
 *   parseTimeToDate("12:00 am") -> Date object for today at 00:00
 *   parseTimeToDate("12:30 pm") -> Date object for today at 12:30
 *   parseTimeToDate("10:05")    -> Date object for today at 10:05 (24hr format)
 *
 * If a baseDate is provided, the returned Date will have the same year/month/day as baseDate.
 */
/**
 * Formats a time string that may contain time ranges or single times
 * Handles formats like "6:56 - 7:57 pm", "9:15", "10am", "12:45", "18:21", "06:59 am"
 * @param {string} value - The time string to format
 * @returns {string} - Formatted time string
 */
export function formatTimeString(value) {
    if (!value) return '';

    // Handle time ranges like "6:56 - 7:57 pm"
    if (typeof value === 'string' && value.includes(' - ')) {
        return value.replace(/(\d{1,2}:\d{2})\s*(am|pm)?/gi, (match, time, ampm) => {
            try {
                const [hours, minutes] = time.split(':').map(Number);
                let hour24 = hours;

                if (ampm) {
                    const isPM = ampm.toLowerCase() === 'pm';
                    hour24 = isPM && hours !== 12 ? hours + 12 : (!isPM && hours === 12 ? 0 : hours);
                }

                const date = new Date();
                date.setHours(hour24, minutes, 0, 0);
                return formatTime(date);
            } catch (e) {
                return match; // Return original if parsing fails
            }
        });
    }

    // Handle times without colons like "10am", "5pm", "12pm"
    if (typeof value === 'string' && /^\d{1,2}(am|pm)$/i.test(value)) {
        try {
            const timeMatch = value.match(/(\d{1,2})(am|pm)/i);
            if (timeMatch) {
                const hours = parseInt(timeMatch[1], 10);
                const ampm = timeMatch[2].toLowerCase();
                
                let hour24 = hours;
                const isPM = ampm === 'pm';
                hour24 = isPM && hours !== 12 ? hours + 12 : (!isPM && hours === 12 ? 0 : hours);
                
                const date = new Date();
                date.setHours(hour24, 0, 0, 0);
                return formatTime(date);
            }
            return value;
        } catch (e) {
            return value;
        }
    }

    // Handle single times like "9:15", "10am", "12:45", "18:21", "06:59 am"
    if (typeof value === 'string' && /^\d{1,2}:\d{2}/.test(value)) {
        try {
            // Extract time and am/pm parts
            const timeMatch = value.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/i);
            if (timeMatch) {
                const hours = parseInt(timeMatch[1], 10);
                const minutes = parseInt(timeMatch[2], 10);
                const ampm = timeMatch[3];

                let hour24 = hours;
                if (ampm) {
                    const isPM = ampm.toLowerCase() === 'pm';
                    hour24 = isPM && hours !== 12 ? hours + 12 : (!isPM && hours === 12 ? 0 : hours);
                } else {
                    // European logic: 12+ is PM, before 12 is AM
                    if (hours >= 12) {
                        hour24 = hours; // Already in 24-hour format
                    } else {
                        hour24 = hours; // Keep as AM
                    }
                }

                const date = new Date();
                date.setHours(hour24, minutes, 0, 0);
                return formatTime(date);
            }
            return value; // Return original if parsing fails
        } catch (e) {
            return value; // Return original if parsing fails
        }
    }

    // For other values (like Hebrew text), return as-is
    return value;
}

export function parseTimeToDate(timeString, baseDate) {
    //console.log(timeString, baseDate);
    // Attempt to match a typical 12-hour pattern, e.g. "05:10 pm"
    try {
        const match = timeString.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
        if (!match) {
            return null; // not a parseable time
        }
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const ampm = match[3].toLowerCase();

        // Convert to 24-hour
        let finalHour = hours;
        if (ampm === 'pm' && hours < 12) {
            finalHour += 12;
        } else if (ampm === 'am' && hours === 12) {
            finalHour = 0;
        }

        let date;
        if (baseDate) {
            date = new Date(baseDate.getTime());
        } else {
            date = new Date();
        }
        date.setHours(finalHour);
        date.setMinutes(minutes);
        date.setSeconds(0);
        return date;
    } catch (er) {
        console.error(er);
        return timeString;
    }
}

export function getDate() {
    const params = new URLSearchParams(window.location.search);
    let finalDate = null;
    const now = new Date();

    // Check if Gregorian date (d=YYYY-MM-DD) is provided.
    if (params.has("d")) {
        const gregorianDateStr = params.get("d");
        const [year, month, day] = gregorianDateStr.split("-").map(Number);
        const parsedDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());

        if (!isNaN(parsedDate.getTime())) {
            //console.log("Using Gregorian Date from Query:", parsedDate.toLocaleDateString());
            finalDate = parsedDate;
        } else {
            console.error("Invalid Gregorian Date:", gregorianDateStr);
        } 
    } 

    // If no Gregorian date provided, check if Hebrew date (h=YYYY-MM-DD) is provided.
    if (!finalDate && params.has("h")) {
        const hebrewDateStr = params.get("h");
        const [year, month, day] = hebrewDateStr.split("-").map(Number);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            //console.log("Using Hebrew Date from Query:", day, month, year);
            const hebrewDate = new HDate(day, month, year);
            finalDate = hebrewDate.greg();
           // console.log("Converted Hebrew Date to Gregorian:", finalDate);
        } else {
            console.error("Invalid Hebrew Date:", hebrewDateStr);
        }
    }

    // If no date was provided, default to today's date.
    if (!finalDate) {
        finalDate = new Date();
       // console.log("Using Default Date:", finalDate.toString());
    }

    // If a time parameter (t) is provided, adjust the finalDate's time accordingly.
    if (params.has("t")) {
        const timeStr = params.get("t");
        finalDate = parseTimeToDate(timeStr, finalDate);
        //console.log("Adjusted Date with time:", finalDate.toString());
    }

    return finalDate;
}

export function getCurrentItemIndex(schedule, baseDate) {
    // Get today's date in YYYY-MM-DD format for comparison (using local time, not UTC)
    const today = new Date(baseDate);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
        
    // Get zmanim data for the base date to handle zman and relativeZman fields
    const zmanimData = getDailyZmanim(baseDate);
        
    // Parse and categorize all schedule items
    const allTimes = schedule
      .map((item, i) => {
        try {
          // For items without a date, use the base date from getDate()
          const itemDate = item.date || todayStr;
          
          // Create the appropriate base date for parsing the time
          let itemBaseDate = new Date(baseDate);
          if (item.date) {
            const [year, month, day] = item.date.split('-').map(Number);
            itemBaseDate = new Date(year, month - 1, day);
            itemBaseDate.setHours(baseDate.getHours(), baseDate.getMinutes(), baseDate.getSeconds());
          }
          
          let parsedDate = null;
          
          // Handle different time field types
          if (item.value) {
            // Direct time value
            parsedDate = parseTimeToDate(item.value, itemBaseDate);
          } else if (item.zman) {
            // Zman field - get the time from zmanim data
            const zmanTime = zmanimData.getZman(item.zman);
            if (zmanTime) {
              parsedDate = new Date(zmanTime);
            }
          } else if (item.relativeZman) {
            // Relative zman field - calculate based on base zman
            const baseZman = zmanimData.getZman(item.relativeZman);
            if (baseZman) {
              const adjustedTime = new Date(baseZman.getTime());
              const minutes = item.relativeMinutes || 0;
              const direction = item.relativeDirection;
              
              // Add or subtract minutes based on the direction
              const minutesInMs = minutes * 60 * 1000;
              if (direction === 'before' || direction === 'before_exact') {
                adjustedTime.setTime(adjustedTime.getTime() - minutesInMs);
              } else if (direction === 'after' || direction === 'after_exact') {
                adjustedTime.setTime(adjustedTime.getTime() + minutesInMs);
              }
              
              parsedDate = adjustedTime;
            }
          }
          
          if (!parsedDate) return null;
          
          // Handle midnight crossing for items without explicit dates
          // If the parsed time is earlier in the day than the base time, 
          // and the base time is late at night (after 6 PM), assume the event is for the next day
          if (!item.date && parsedDate < baseDate && baseDate.getHours() >= 18 && parsedDate.getHours() < 6) {
            parsedDate = new Date(parsedDate.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
          }
                    
          return { 
            index: i, 
            date: parsedDate, 
            itemDate: itemDate,
            isToday: itemDate === todayStr,
            isFuture: itemDate > todayStr,
            isPast: itemDate < todayStr
          };
        } catch (err) {
          console.error('Error parsing time for schedule item', item, err);
          return null;
        }
      })
      .filter(t => t !== null);
    
    if (!allTimes.length) {
      return null; // No valid times at all
    }
    
    // Check if all items are for future dates
    const allFutureDates = allTimes.every(t => t.isFuture);
    if (allFutureDates) {
      // When showing a future date's schedule, highlight the earliest item for that date
      const sortedByTime = allTimes.sort((a, b) => a.date - b.date);
      return sortedByTime[0].index;
    }
    
    // Check if all items are for past dates
    const allPastDates = allTimes.every(t => t.isPast);
    if (allPastDates) {
      // When showing a past date's schedule, highlight the latest item for that date
      const sortedByTime = allTimes.sort((a, b) => b.date - a.date);
      return sortedByTime[0].index;
    }
    
    // Filter items that are relevant for today's highlighting logic
    const todayItems = allTimes.filter(t => t.isToday).sort((a, b) => a.date - b.date);
    
    if (todayItems.length === 0) {
      // No items for today, find the chronologically earliest available item
      // Prefer future items over past items
      const futureItems = allTimes.filter(t => t.date > baseDate);
      if (futureItems.length > 0) {
        futureItems.sort((a, b) => a.date - b.date);
        return futureItems[0].index;
      } else {
        // If no future items, fall back to all items sorted chronologically
        allTimes.sort((a, b) => a.date - b.date);
        return allTimes[0].index;
      }
    }
    
    // Apply time-based highlighting logic for today's items
    const nextEventToday = todayItems.find(t => t.date > baseDate);
    const currentEvent = [...todayItems].reverse().find(t => t.date <= baseDate);
        // If no event has started yet, return the first upcoming event for today
    if (!currentEvent) {
      return todayItems[0].index;
    }
    
    // Define a 5-minute grace period from the start of the current event
    const graceEnd = new Date(currentEvent.date.getTime() + 5 * 60 * 1000);
    
    // If we're still within the grace period, highlight the current event
    if (baseDate < graceEnd) {
      return currentEvent.index;
    } else if (nextEventToday) {
      // If we've passed the grace period and there is a next event today, highlight that event
      return nextEventToday.index;
    } else {
      // If there's no upcoming event for today, check for future-dated items
      const futureItems = allTimes.filter(t => t.isFuture && t.date > baseDate);
      if (futureItems.length > 0) {
        futureItems.sort((a, b) => a.date - b.date);
        return futureItems[0].index;
      } else {
        // If no future items available, check if we're past the last event of the day
        const lastEventToday = todayItems[todayItems.length - 1];
        if (lastEventToday) {
          // Add a 2-hour buffer after the last event of the day
          const endOfDayBuffer = new Date(lastEventToday.date.getTime() + 2 * 60 * 60 * 1000);
          
          if (baseDate > endOfDayBuffer) {
            return null;
          } else {
            // If we're within the buffer period, continue highlighting the current event
            return currentEvent.index;
          }
        } else {
          // If no events for today, don't highlight anything
          return null;
        }
      }
    }
  }
  
export function isInDisplayWindow(displayDateString, expirationDateString) {
    const now = getDate();
   // console.log('isInDisplayWindow', now, displayDateString, expirationDateString);
    if (!displayDateString && !expirationDateString) {
        return true; // Always show if neither are set
    }

    if (!displayDateString) {
        const expirationDate = new Date(expirationDateString);
        return now <= expirationDate; // Show until expirationDate
    }

    if (!expirationDateString) {
        const displayDate = new Date(displayDateString);
        return now >= displayDate; // Show from displayDate onwards
    }
    const displayDate = new Date(displayDateString);
    const expirationDate = new Date(expirationDateString);
//console.log('now>display', now >= displayDate)
//console.log('now<expiration', now <= expirationDate)
    let isInWindow = now >= displayDate && now <= expirationDate;
    return isInWindow; // Show between display and expiration
}



export function getLastScheduleTime(schedule) {
    if (!schedule || !schedule.schedule || schedule.schedule.length === 0) return null;

    let latestTime = null;

    schedule.schedule.forEach((item) => {
        if (item.time) {
            const parsedTime = parseScheduleTime(item.time);
            if (parsedTime && (!latestTime || parsedTime > latestTime)) {
                latestTime = parsedTime;
            }
        }
    });

    return latestTime;
}

export function parseScheduleTime(timeStr) {
    if (!timeStr) return null;

    const now = getDate();
    const [hourMin, period] = timeStr.split(" "); // Split time and AM/PM
    const [hour, minute] = hourMin.split(":").map(Number);

    let hours = hour;
    if (period && period.toLowerCase() === "pm" && hour !== 12) {
        hours += 12; // Convert PM times to 24-hour format
    } else if (period && period.toLowerCase() === "am" && hour === 12) {
        hours = 0; // Midnight case
    }

    const dateObj = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minute, 0);
    return dateObj;
}

export function reloadWithNextSaturday() {
    const now = getDate();
    const day = now.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  
    // Calculate days until next Saturday.
    // If today is Saturday, set next Saturday to 7 days ahead.
    const daysUntilSaturday = day === 6 ? 7 : 6 - day;
  
    // Compute next Saturday's date.
    const nextSaturday = new Date(now);
    nextSaturday.setDate(now.getDate() + daysUntilSaturday);
  
    // Format the date as "yyyy-mm-dd".
    const yyyy = nextSaturday.getFullYear();
    let mm = nextSaturday.getMonth() + 1; // Months are zero-indexed.
    let dd = nextSaturday.getDate();
    mm = mm < 10 ? `0${mm}` : mm;
    dd = dd < 10 ? `0${dd}` : dd;
    const formattedDate = `${yyyy}-${mm}-${dd}`;
  
    // Update the URL parameter "d" with the formatted date and reload.
    const url = new URL(window.location.href);
    url.searchParams.set('d', formattedDate);
    window.location.href = url.toString();
  }

  export function formatDateRangeEnglish(startDate, endDate) {
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const startMonth = startDate.toLocaleString('en-US', { month: 'long' });
    const endMonth = endDate.toLocaleString('en-US', { month: 'long' });
    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }
  }
  
  export function formatDateRangeJewish(startDate, endDate) {
    // Create HDate instances for start and end.
    const hStart = new HDate(startDate);
    const hEnd = new HDate(endDate);
    // Use gematriya to convert numeric day to Hebrew letters.
    const startDayHeb = gematriya(hStart.getDate());
    const endDayHeb = gematriya(hEnd.getDate());
    // Assuming HDate has a method getMonthName(lang) that returns the month's name.
    const startMonthHeb = getHebrewMonthName(hStart);
    const endMonthHeb = getHebrewMonthName(hEnd);
      if (startMonthHeb === endMonthHeb) {
      return `${startDayHeb}-${endDayHeb} ${startMonthHeb}`;
    } else {
      return `${startDayHeb} ${startMonthHeb} - ${endDayHeb} ${endMonthHeb}`;
    }
  }
  
  function getHebrewMonthName(hDate) {
    const rendered = hDate.render('he', false);
    return rendered.split(' ')[1] || '';
  }

  export function formatDateJewish(date) {
    const hDate = new HDate(date);
    const dayInHebrew = gematriya(hDate.getDate());
    const monthName = getHebrewMonthName(hDate);
    return `${dayInHebrew} ${monthName}`;
  }
